import type { SkyBadgeColor } from "@skypointcloud/skypoint-ui";
import type { TransformationGraph, DbtJobRunRow } from "@shared/types/transformations";
import { dbtModelCountsOf } from "@shared/types/dbtModelCounts";
import { isInlinedRow, modelRows, testTally } from "./modelGroups";
import { runOutcome, skipReasonWord } from "./runOutcome";

/**
 * The Transformations landing's facts strip and the counts on its view switch (owner feedback
 * 2026-09-28: the one-line "Repository connected · main · last run succeeded · 4 models · 23 tests
 * passed" read as plain text, and "Models 5" beside "4 models" did not explain itself).
 *
 * Four facts, each a short value the screen renders with design-system parts:
 * - Repository: the ref, connected or not (colour and word ride together on the screen).
 * - Last run: the outcome's word and colour from `runOutcome` (the History view's vocabulary),
 *   the instant it is about, and the run it links to.
 * - Models: "{built} of {selected} built" (round 6, W6): built is the models the newest finished
 *   step SUCCEEDED on (T5, `dbtModelCountsOf`; an older row keeps reported minus failed), and
 *   selected is what that run selected, never the whole project. Failed, skipped, "Not run {n}"
 *   and "Not in this run {n}" are the chips: "Not run" is a model the run chose that dbt did not
 *   run, and "Not in this run" (#1213) is a project model the run did not choose
 *   (`projectCounts().models`, the number the switch shows, less the ephemeral models dbt inlines
 *   and never builds, #1437, minus selected), so built + failed + skipped + not run + not in this
 *   run + inlined is the switch's count and a run that built all it chose never shows an
 *   unexplained "Not run".
 * - Tests: "{passed} of {ran} passed" over the tests the newest run ran (#1213: the same basis
 *   as Models, the run's own), counting only results from the newest run, as the Overview does
 *   (round 10, B-29: a disabled job's old results are never passed or failed here). Failed
 *   (errored included), warned, skipped and "Not in this run {n}" are badges, so they add up to
 *   the switch's count. #1213 round 4: "Not in this run" is every project test the newest run did
 *   not run (the project's count minus the run's tests), the tests with a result from an earlier
 *   run included; how many of them have one is the chip's hint ("8 of these have results from
 *   earlier runs."), never a chip of its own. The Tests view's basis is the last 30 runs, so its
 *   word for a test with no result is "No recent result", never this one. Only without the
 *   project's list is it the newest run's own count, passed being succeeded minus warned (the
 *   image counts a warn as succeeded).
 *
 * Pure: from the graph (read once per visit), its read state and the newest transform step, as
 * `landingStatus.ts` is. Nothing here reads the audited binding.
 */

export interface ProjectCounts {
  /** Models, seeds and snapshots of the project, disabled ones included (the Models view's rows). */
  models: number;
  /** Of `models`, the ephemeral ones dbt inlines and never builds (#1437). */
  inlined: number;
  /** Every test the Tests view lists. */
  tests: number;
}

/** The project's counts, once a run's manifest was read; null before (the switch shows none). */
export function projectCounts(graph: TransformationGraph | undefined): ProjectCounts | null {
  if (graph?.run == null || graph.project === null) return null;
  const rows = modelRows(graph.nodes, graph.disabledNodes ?? []);
  return {
    models: rows.length,
    // #1437: an ephemeral model is inlined by dbt and never built, so it is not a model that
    // "was not in this run". It stays in `models`, the Models view's count.
    inlined: rows.filter(isInlinedRow).length,
    tests: graph.tests.length,
  };
}

export interface FactBadge {
  label: string;
  color: SkyBadgeColor;
  /** A sentence that explains the count, shown on hover and focus and read with the badge. */
  hint?: string;
}

/** A count fact: its words, whether they say an absence (muted), and its status badges. */
export interface CountFact {
  text: string;
  muted: boolean;
  badges: FactBadge[];
}

export interface RepositoryFact {
  /** `unknown` when the graph (which carries the binding) could not be read. */
  state: "connected" | "not_connected" | "unknown";
  ref: string | null;
  /** Round 7 T-38: the bound repository's address, which the strip names and links. */
  repoUrl: string | null;
  /** #1306: connected, but the address is Instance admin and above (`bundle.repoUrlWithheld`). */
  withheld?: boolean;
  /**
   * #1674: bound, but the newest run stopped at the checkout (a branch that is not there, a token
   * the repository refused), so the strip never reads "Connected" beside it.
   */
  cloneFailed?: boolean;
}

export interface LastRunFact {
  /** The run the fact links to; null when there is none to open. */
  runId: string | null;
  /** The outcome's word ("Succeeded", "Could not clone"); null when there is no run to name. */
  word: string | null;
  color: SkyBadgeColor;
  /** The instant the relative time is of; null for a skip, which has none worth showing. */
  at: string | null;
  /** True while running: the instant is when it started, read "started 2 minutes ago". */
  started: boolean;
  /** Words in place of, or after, the outcome: a skip's reason, "No run yet". */
  note: string | null;
}

export interface LandingFacts {
  repository: RepositoryFact;
  lastRun: LastRunFact;
  models: CountFact;
  tests: CountFact;
}

export interface FactsInput {
  graph: TransformationGraph | undefined;
  graphState: "loading" | "ready" | "error";
  newest: DbtJobRunRow | null;
  runsState: "loading" | "ready" | "error";
}

const UNREADABLE = "Could not be read";

function muted(text: string): CountFact {
  return { text, muted: true, badges: [] };
}

function fact(text: string): CountFact {
  return { text, muted: false, badges: [] };
}

function repositoryFact({ graph, graphState, newest }: FactsInput): RepositoryFact {
  const bundle = graph?.bundle;
  if (!bundle || graphState === "error") return { state: "unknown", ref: null, repoUrl: null };
  // #1674 review: only while the failed checkout tried the branch saved now. A branch changed
  // after it has not been tried, so it reads Connected until the next run says otherwise.
  const cloneFailed =
    newest?.stepStatus === "failed" &&
    newest.exitClass === "clone" &&
    newest.checkoutRefCurrent !== false;
  return bundle.repoBound
    ? {
        state: "connected",
        ref: bundle.ref,
        repoUrl: bundle.repoUrl,
        ...(bundle.repoUrlWithheld ? { withheld: true } : {}),
        ...(cloneFailed ? { cloneFailed: true } : {}),
      }
    : { state: "not_connected", ref: null, repoUrl: null };
}

function lastRunFact({ newest, runsState }: FactsInput): LastRunFact {
  const none = { runId: null, word: null, color: "subtle" as const, at: null, started: false };
  if (runsState === "error") return { ...none, note: UNREADABLE };
  if (!newest) return { ...none, note: "No run yet" };
  const outcome = runOutcome(newest);
  const base = { runId: newest.runId, word: outcome.word, color: outcome.color };
  switch (outcome.kind) {
    case "queued":
      return { ...base, at: newest.createdAt, started: false, note: null };
    case "running":
      // #1214: the run's own start, the one the Runs list shows, never the later dbt step start.
      return {
        ...base,
        at: newest.runStartedAt ?? newest.startedAt ?? newest.createdAt,
        started: true,
        note: null,
      };
    case "skipped":
      return { ...base, at: null, started: false, note: skipReasonWord(newest.skippedReason) };
    default:
      return {
        ...base,
        at: newest.finishedAt ?? newest.startedAt ?? newest.createdAt,
        started: false,
        note: null,
      };
  }
}

/** The newest step, when it has finished (a step in flight reports no counts yet). */
function finished(newest: DbtJobRunRow | null): DbtJobRunRow | null {
  return newest && !runOutcome(newest).inFlight ? newest : null;
}

/**
 * What a count fact says when neither the run nor the project gives a number: the graph could not
 * be read, or nothing has been built or run yet.
 */
function noCount(input: FactsInput, absent: string): CountFact {
  return muted(input.graphState === "error" ? UNREADABLE : absent);
}

function modelsFact(input: FactsInput, counts: ProjectCounts | null): CountFact {
  const total = counts?.models ?? null;
  const row = finished(input.newest);
  const models = row ? dbtModelCountsOf(row) : null;
  if (!models) {
    return total === null ? noCount(input, "Not built yet") : fact(`${total} in the project`);
  }
  const badges: FactBadge[] = [];
  if (models.failed > 0) badges.push({ label: `${models.failed} failed`, color: "danger" });
  if (models.skipped > 0) badges.push({ label: `${models.skipped} skipped`, color: "warning" });
  // Without the project's list (not read yet, or unreadable), or when a run newer than the list
  // selected more than it names, only what the run itself did not run is counted.
  // #1213: "Not run" is a model the run chose that dbt did not run; a model the run did not
  // choose is "Not in this run". The two never share a word, so "4 of 4 built" never sits beside
  // a "Not run 1" that the run's own record does not have.
  const buildable = total === null ? null : total - (counts?.inlined ?? 0);
  const unselected =
    buildable !== null && buildable >= models.selected ? buildable - models.selected : 0;
  if (models.notRun > 0) badges.push({ label: `Not run ${models.notRun}`, color: "subtle" });
  if (unselected > 0) badges.push({ label: `Not in this run ${unselected}`, color: "subtle" });
  return { text: `${models.built} of ${models.selected} built`, muted: false, badges };
}

/**
 * #1213 round 4: the tests the newest run did not run, one chip; how many of them have a result
 * from an earlier run is its hint, never a chip beside it.
 */
function notInThisRun(count: number, earlier: number): FactBadge | null {
  if (count <= 0) return null;
  const badge: FactBadge = { label: `Not in this run ${count}`, color: "subtle" };
  const some = Math.min(earlier, count);
  if (some <= 0) return badge;
  return {
    ...badge,
    hint:
      some === 1
        ? "1 of these has a result from an earlier run."
        : `${some} of these have results from earlier runs.`,
  };
}

/** D5: the Tests view's tally over the project's tests, the switch's count the denominator. */
function talliedTestsFact(
  graph: TransformationGraph,
  total: number,
  row: DbtJobRunRow | null,
): CountFact {
  // Round 10 (B-29, regression): the strip counts the newest run's results only. A test whose latest
  // result is from an older run is not passed or failed here. `graph.run` is the newest launched
  // transform across the instance, so any job's older results (a dbt job that is disabled now, or
  // runs less often) are not in this run, and a stale "4 failed" cannot sit beside an Overview
  // that says the run succeeded. #1213 round 4: they are counted in "Not in this run", and the
  // chip's hint says how many of them have a result from an earlier run. The Tests view keeps each
  // test's latest result across runs (D5) and says which run it is from.
  const currentRunId = graph.run?.runId ?? null;
  const isEarlier = (test: TransformationGraph["tests"][number]): boolean =>
    currentRunId !== null &&
    test.status == null &&
    !!test.lastResult &&
    test.lastResult.runId !== currentRunId;
  const earlier = graph.tests.filter(isEarlier).length;
  const tally = testTally(graph.tests.filter((test) => !isEarlier(test)));
  const failed = tally.failed + tally.errored;
  const ranAny = failed + tally.passed + tally.warned + tally.skipped;
  const notInRun = notInThisRun(Math.max(0, total - ranAny), earlier);
  if (ranAny === 0) {
    const none = muted(row && row.testsTotal === 0 ? "No tests ran" : "Not run yet");
    return earlier > 0 && notInRun ? { ...none, badges: [notInRun] } : none;
  }
  const badges: FactBadge[] = [];
  if (failed > 0) badges.push({ label: `${failed} failed`, color: "danger" });
  if (tally.warned > 0) badges.push({ label: `${tally.warned} warned`, color: "warning" });
  if (tally.skipped > 0) badges.push({ label: `${tally.skipped} skipped`, color: "subtle" });
  if (notInRun) badges.push(notInRun);
  // #1213: passed of the tests the run ran, the same basis as "4 of 4 built". The project's other
  // tests are the "Not in this run" chip, so passed + failed + warned + skipped + that chip is the
  // switch's count.
  return { text: `${tally.passed} of ${ranAny} passed`, muted: false, badges };
}

function testsFact(input: FactsInput, counts: ProjectCounts | null): CountFact {
  const total = counts?.tests ?? null;
  const row = finished(input.newest);
  // #1213: while the newest run is queued or running it has no results yet, so Tests reads on
  // Models' basis ("{n} in the project"), never an earlier run's tally beside "5 in the project".
  // A skipped run ran nothing and reports no model counts, so Models reads the same way.
  if (input.newest && total !== null && (!row || runOutcome(row).kind === "skipped")) {
    return fact(`${total} in the project`);
  }
  if (total !== null && input.graph?.project != null) {
    return talliedTestsFact(input.graph, total, row);
  }
  const inProject =
    total === null ? noCount(input, "Not run yet") : fact(`${total} in the project`);
  if (!row) return inProject;
  if (row.testsTotal === 0) return muted("No tests ran");

  const failed = row.testsFailed ?? 0;
  const warned = row.testsWarned ?? 0;
  const badges: FactBadge[] = [];
  if (failed > 0) badges.push({ label: `${failed} failed`, color: "danger" });
  if (warned > 0) badges.push({ label: `${warned} warned`, color: "warning" });
  if (row.testsSucceeded === null) {
    // No passed count: the badges alone say what is known ("2 failed"), never "Not run yet".
    return badges.length > 0 && total === null
      ? { text: "", muted: false, badges }
      : { ...inProject, badges };
  }

  const passed = Math.max(0, row.testsSucceeded - warned);
  const ran = row.testsTotal ?? row.testsSucceeded + failed;
  // #1213: the tests the run ran, the same basis as the tallied path above.
  return { text: `${passed} of ${ran} passed`, muted: false, badges };
}

/**
 * `counts` is the project count the view switch shows; the page passes its memoised value so the
 * strip's "of 5" and the switch's "Models 5" are one number by construction.
 */
export function landingFacts(
  input: FactsInput,
  counts: ProjectCounts | null = projectCounts(input.graph),
): LandingFacts {
  return {
    repository: repositoryFact(input),
    lastRun: lastRunFact(input),
    models: modelsFact(input, counts),
    tests: testsFact(input, counts),
  };
}
